import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { htmlSafe } from '@ember/template';

import { t, type IntlService } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import { PhSealCheck, PhCurrencyCircleDollar } from 'ember-phosphor-icons';
import Chip from 'tio-reimagined/components/chip';
import { VStack } from 'tio-ui/components/layout';
import type CompanySettingModel from '../../models/company-setting';
import type CompanyLpnSetting from '../../models/company-lpn-setting';
import type LpnPage from '../../models/lpn-page';

interface LpnCardSignature {
  Args: {
    lpn: LpnPage;
    companySettings: CompanySettingModel;
  };
  Element: HTMLElement;
  Blocks: Record<string, never>;
}

export default class LpnCard extends Component<LpnCardSignature> {
  @service declare intl: IntlService;

  companyLpnSettingState = trackedFunction(this, async () => {
    return await this.args.lpn.settingForCompany(this.args.companySettings);
  });

  get companyLpnSetting() {
    return this.companyLpnSettingState.value as CompanyLpnSetting | undefined;
  }

  get mergedLpn() {
    const overrides = (this.companyLpnSetting ?? {}) as Record<string, unknown>;
    return new Proxy(this.args.lpn as LpnPage, {
      get(target, prop: string | symbol) {
        if (typeof prop === 'string') {
          const value = overrides[prop];
          // Only use the company-lpn-setting value if it's set (not null/undefined).
          // null/undefined = "not overridden, inherit from global LPN page".
          // false, 0, '' are valid override values (e.g. deliberately disabling a badge).
          if (value !== null && value !== undefined) {
            return value;
          }
          return (target as unknown as Record<string, unknown>)[prop];
        }
        return (target as unknown as Record<symbol, unknown>)[prop];
      },
    }) as LpnPage;
  }

  /**
   * The "top badge" is the first of capped-tuition / savings-percent / savings-amount
   * that the LPN has. It renders prominently below the logo (next to Employer Pick)
   * and is excluded from the tag cloud to avoid duplication.
   */
  get topBadge() {
    return this.cappedTuitionBadge ?? this.savingsPercentBadge ?? this.savingsAmountBadge ?? null;
  }

  get providedBy() {
    return this.mergedLpn.providedBy?.trim();
  }

  get descriptionHtml() {
    // Use companyLpnSetting description if it has blocks, otherwise fall back to lpnPage description
    const companyBlocks = this.companyLpnSetting?.description?.blocks ?? [];
    const lpnBlocks = this.args.lpn.description?.blocks ?? [];
    const blocks = companyBlocks.length > 0 ? companyBlocks : lpnBlocks;

    const html: string[] = [];
    for (const block of blocks) {
      switch (block.type) {
        case 'paragraph':
          html.push(`<p>${block.data?.text ?? ''}</p>`);
          break;
        case 'header': {
          const level = block.data?.level ?? 3;
          html.push(`<h${level}>${block.data?.text ?? ''}</h${level}>`);
          break;
        }
        case 'list': {
          const tag = block.data?.style === 'ordered' ? 'ol' : 'ul';
          const items = (block.data?.items ?? [])
            .map(
              (item: string | { content?: string }) =>
                `<li>${typeof item === 'string' ? item : (item?.content ?? '')}</li>`
            )
            .join('');
          html.push(`<${tag}>${items}</${tag}>`);
          break;
        }
        default:
          if (block.data?.text) {
            html.push(`<p>${block.data.text}</p>`);
          }
          break;
      }
    }
    return htmlSafe(html.join(''));
  }

  get cappedTuitionBadge() {
    if (!this.mergedLpn.badgeCappedTuition) {
      return null;
    }
    const range = (this.mergedLpn.badgeCappedTuitionRange ?? '').trim();
    if (range.length > 0) {
      return `${this.intl.t('lpn.badges.capped_tuition_at')} $${range}`;
    }
    return this.intl.t('lpn.badges.capped_tuition');
  }

  get savingsPercentBadge() {
    if (this.mergedLpn.useDiscountPercentRange) {
      return `${this.intl.t('lpn.badges.save')} ${this.mergedLpn.discountPercentRange}%`;
    }
    if (this.mergedLpn.badgeDiscount) {
      return `${this.intl.t('lpn.badges.save')} ${this.mergedLpn.discountPercent}%`;
    }
    return null;
  }

  get savingsAmountBadge() {
    if (this.mergedLpn.useSaveDollarAmountRange) {
      return `${this.intl.t('lpn.badges.save')} $${this.mergedLpn.saveDollarAmountRange}`;
    }
    if (this.mergedLpn.saveDollarAmount) {
      return `${this.intl.t('lpn.badges.save')} $${this.mergedLpn.saveDollarAmountValue}`;
    }
    return null;
  }

  get costPerCreditBadge() {
    if (this.mergedLpn.useCostPerCreditRange) {
      return `${this.intl.t('lpn.badges.cost_per_credit')}: $${this.mergedLpn.costPerCreditRange}`;
    }
    if (this.mergedLpn.costPerCredit && this.mergedLpn.costPerCredit.length > 0) {
      return `${this.intl.t('lpn.badges.cost_per_credit')}: $${this.mergedLpn.costPerCredit}`;
    }
    return null;
  }
  get tags() {
    const promoted = this.topBadge;
    return [
      // Capped Tuition (skip if promoted to top badge)
      promoted !== this.cappedTuitionBadge ? this.cappedTuitionBadge : null,
      // Savings Percent (skip if promoted to top badge)
      promoted !== this.savingsPercentBadge ? this.savingsPercentBadge : null,
      // Savings Amount (skip if promoted to top badge)
      promoted !== this.savingsAmountBadge ? this.savingsAmountBadge : null,
      // Part Tuition Waived
      this.mergedLpn.partTuitionWaiver ? this.intl.t('lpn.badges.part_tuition_waiver') : null,
      // Low Tuition
      this.mergedLpn.lowTuition ? this.intl.t('lpn.badges.low_tuition') : null,
      // Family Eligible
      this.mergedLpn.badgeFamilySavings ? this.intl.t('lpn.badges.family_eligible') : null,
      // No Fees
      this.mergedLpn.badgeFeesWaived ? this.intl.t('lpn.badges.no_fees') : null,
      // Cost Per Credit
      this.costPerCreditBadge,
      // Formats
      ...(this.mergedLpn.format ?? []),
      // Programs
      ...(this.mergedLpn.programs ?? []),
    ].filter(Boolean);
  }
  <template>
    <a
      href={{this.mergedLpn.url}}
      target="_blank"
      rel="noopener noreferrer"
      class="block no-underline"
    >
      <VStack
        @collapsed={{true}}
        class="border border-gray-200 rounded-2xl bg-white p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      >
        <div class="flex items-start justify-between">
          <div class="flex items-center gap-3">
            {{#if this.mergedLpn.imageUrl}}
              {{! <div class="flex h-12 w-40 items-center justify-center rounded border border-gray-100 bg-white"> }}
              <img
                src="{{this.mergedLpn.imageUrl}}"
                alt="{{this.mergedLpn.schoolName}}"
                width="275"
                height="90"
              />
              {{! </div> }}
            {{/if}}
          </div>
          {{! <button
          type="button"
          class="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-400"
          aria-label="Favorite"
        >
          <PhStar @size="18" />
        </button> }}
        </div>

        <div class="flex min-h-[10px] flex-wrap gap-2 text-sm text-gray-700">
          {{#if this.mergedLpn.employerRecommended}}
            <div class="flex items-center text-md font-semibold">
              <PhSealCheck @size="20" class="text-gray-500" @weight="bold" />
              <span>{{t "lpn.employer_recommended"}}</span>
            </div>
          {{/if}}
          {{#if this.topBadge}}
            <div class="flex items-center text-md font-semibold">
              <PhCurrencyCircleDollar @size="20" class="text-gray-500" @weight="bold" />
              <span>{{this.topBadge}}</span>
            </div>
          {{/if}}
        </div>

        <h3 class="mt-1 text-xl font-semibold text-gray-900">{{this.mergedLpn.schoolName}}</h3>

        <div class="min-h-[14px]">
          {{#if this.providedBy}}
            <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {{t "lpn.provided_by"}}
              <span class="text-gray-700">{{this.providedBy}}</span>
            </p>
          {{/if}}
        </div>

        <div class="mt-1 min-h-[84px] text-sm text-gray-600 prose prose-sm font-normal">
          {{this.descriptionHtml}}
        </div>

        <div class="mt-3 min-h-[20px]">
          {{#if this.tags.length}}
            <div class="flex flex-wrap gap-2">
              {{#each this.tags as |tag|}}
                <Chip @appearance="outlined" class="bg-gray-50 text-gray-700">
                  <span class="text-xs whitespace-nowrap">{{tag}}</span>
                </Chip>
              {{/each}}
            </div>
          {{/if}}
        </div>
      </VStack>
    </a>
  </template>
}
